import { DamageIcon } from '@lmc/design-system'
import { DamageTypeEnum } from '@lmc/validations'
import { Form as AntdForm, Input, Select } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import StepDrawer from '../../StepDrawer'
import StepEntryCard, {
  StepEntryCardItem,
  StepEntryList,
  useEntryDrawer
} from '../../StepEntryCard'
import StepField from '../../StepField'
import { keyOptions } from '../../stepOptions'
import type { DamageEntryValue } from './injuryEntryValues'

/** In the order the drawer offers them; the first is pre-selected. */
export const DAMAGE_TYPE_KEYS: Record<DamageTypeEnum, string> = {
  [DamageTypeEnum.ongoingHazard]:
    incidentReportsConstants.CASE_V2_INJURIES_ONGOING_HAZARD,
  [DamageTypeEnum.damageOnly]:
    incidentReportsConstants.CASE_V2_INJURIES_DAMAGE_ONLY
}

type DamageEntriesProps = {
  onChange?: (entries: Array<DamageEntryValue>) => void
  testId: string
  value?: Array<DamageEntryValue>
}

/**
 * What was damaged: a card per damage, whether it left an ongoing hazard and
 * its details, added and edited in a drawer.
 */
const DamageEntries = ({
  onChange,
  testId,
  value = []
}: DamageEntriesProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { close, drawer, open, remove, save } = useEntryDrawer(value, onChange)

  return (
    <div data-testid={testId}>
      <StepEntryList
        addLabel={t(incidentReportsConstants.CASE_V2_INJURIES_ADD_DAMAGES)}
        addTestId={`${testId}-add`}
        onAdd={() => open({ type: DamageTypeEnum.ongoingHazard })}
      >
        {value.map((entry, index) => (
          <StepEntryCard
            // Entries have no id; the list is replaced whole on save.
            // biome-ignore lint/suspicious/noArrayIndexKey: see above
            key={index}
            deleteLabel={t(
              incidentReportsConstants.CASE_V2_INJURIES_DELETE_DAMAGE,
              { damage: t(DAMAGE_TYPE_KEYS[entry.type]) }
            )}
            editLabel={t(
              incidentReportsConstants.CASE_V2_INJURIES_EDIT_DAMAGE,
              { damage: t(DAMAGE_TYPE_KEYS[entry.type]) }
            )}
            icon={<DamageIcon size={24} />}
            title={t(DAMAGE_TYPE_KEYS[entry.type])}
            testId={`${testId}-entry-${index}`}
            onEdit={() => open(entry, index)}
            onDelete={() => remove(index)}
          >
            {entry.details && (
              <StepEntryCardItem
                label={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
              >
                {entry.details}
              </StepEntryCardItem>
            )}
          </StepEntryCard>
        ))}
      </StepEntryList>
      {drawer && (
        <DamageDrawer
          entry={drawer.entry}
          isNew={drawer.index === undefined}
          onClose={close}
          onConfirm={save}
          testId={`${testId}-drawer`}
        />
      )}
    </div>
  )
}

type DamageDrawerProps = {
  entry: DamageEntryValue
  isNew: boolean
  onClose: () => void
  onConfirm: (entry: DamageEntryValue) => void
  testId: string
}

const DamageDrawer = ({
  entry,
  isNew,
  onClose,
  onConfirm,
  testId
}: DamageDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [form] = AntdForm.useForm<DamageEntryValue>()

  return (
    <StepDrawer
      form={form}
      initialValues={entry}
      onCancel={onClose}
      onConfirm={onConfirm}
      testId={testId}
      title={t(
        isNew
          ? incidentReportsConstants.CASE_V2_INJURIES_ADDING_DAMAGES
          : incidentReportsConstants.CASE_V2_INJURIES_EDITING_DAMAGES
      )}
      confirmLabel={t(
        isNew
          ? incidentReportsConstants.CASE_V2_INJURIES_ADD_DAMAGES
          : incidentReportsConstants.CASE_V2_SAVE
      )}
    >
      <StepField
        name="type"
        isRequired
        label={t(incidentReportsConstants.CASE_V2_INJURIES_DAMAGE_TYPE)}
      >
        <Select
          data-testid={`${testId}-type`}
          options={keyOptions(DAMAGE_TYPE_KEYS, t)}
        />
      </StepField>
      <StepField
        name="details"
        label={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
        hint={t(incidentReportsConstants.CASE_V2_INJURIES_DAMAGE_DETAILS_HINT)}
      >
        <Input.TextArea
          data-testid={`${testId}-details`}
          autoSize={{ minRows: 3 }}
        />
      </StepField>
    </StepDrawer>
  )
}

export default DamageEntries
